/**
 * `parseBeat`, `formatBeat`, `formatSec` (W1-01 core-time-math; GRID-03, rpe-editor-features.md E§4.1,
 * DESIGN.md §4.11 "Beat fields echo the parsed value live").
 *
 * Accepted input (whitespace around operators is free; full-width digits and punctuation typed with a CJK IME
 * are read as their ASCII forms):
 *   12:3/4  12 3/4  12+3/4  12 + 3/4  49/4  12          beats (fractions are normalised: 12:6/4 = 13:1/2)
 *   12.75                                              decimal beats      (syntax 'default')
 *   12.1                                               12 + 1/snapDiv     (syntax 'rpe', RPE's "a.k")
 *   12;1                                               12 + 1/snapDiv     (both syntaxes)
 *   12+1/4-1/8                                         sums of the forms above
 *   +1/4  -2  +0.5s                                    relative to ctx.current (0 when absent)
 *   =-2                                                absolute, explicitly signed
 *   23.5s  500ms  1:23.5s  @1:23.5  @83.5              seconds through ctx.tempo, snapped to 1/snapDiv
 *   @37:1/4  @49/4  @12 3/4  @12;1  @37:1/4+2          '@' then a beat term written as a fraction or a snap step
 *   m12  m12+2  mdrop-1/2  m"pre chorus"  @drop        markers through ctx.marker, optionally offset
 * A leading sign applies to the first term only, so "-1:1/2" is minus one and a half and "-1 + 1/2" is minus a
 * half. Errors are MessageRefs of namespace 'time' with the 0-based character index `at`.
 *
 * '@' (the palette's time/marker prefix, DESIGN.md §4.19): '@name' is a marker; '@' followed by a number is a
 * time in seconds (`@83.5`, `@1:23.5`, `@1:00:00`) unless the first number is written as a beat fraction, a
 * mixed number or a snap step (`@37:1/4`, `@49/4`, `@12 3/4`, `@12;1`), which makes the whole input a beat
 * expression (`@37:1/4+2`). `@12+3/4` is an error (12 seconds plus beats); write `@12:3/4`.
 * Clock times check their fields: minutes and seconds after the first field must be below 60 (`1:75s` is an
 * error, `75s` and `75:00` are fine). Seconds are snapped to 1/snapDiv; when snapDiv is not a positive integer
 * (snapping off) they are rounded to 1/1920 like free placement (DESIGN.md §4.12).
 */

import { Rat, type BigRational } from '../math/rational';
import type { FailReason, MessageRef } from '../util/types';
import { beatFromF64Snapped, beatFromRatExact, beatToRat, Beats, negBeat, U32_MAX } from './beat';
import { barBeatParts, type BarBeatParts, type MeterInput, toMeterMap } from './meter';
import { DEFAULT_SNAP_DIV } from './tempo';
import type {
  Beat,
  BeatFormatStyle,
  BeatParseContext,
  BeatParseResult,
  FormatBeat,
  FormatSec,
  ParseBeat,
} from './types';

// ---------------------------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------------------------

/** Internal control flow of the parser; never escapes `parseBeat`. */
class ParseFail extends Error {
  readonly error: FailReason;
  readonly at: number;
  constructor(key: string, at: number, params?: Readonly<Record<string, string | number>>) {
    super(key);
    this.name = 'ParseFail';
    this.error = params === undefined ? { key } : { key, params };
    this.at = at;
  }
}

function fail(key: string, at: number, params?: Readonly<Record<string, string | number>>): never {
  throw new ParseFail(key, at, params);
}

// ---------------------------------------------------------------------------------------------------------------
// Input normalisation (index preserving: every replacement is one UTF-16 unit for one)
// ---------------------------------------------------------------------------------------------------------------

function normalizeChar(code: number): number {
  if (code >= 0xff01 && code <= 0xff5e) return code - 0xfee0; // full-width ASCII
  switch (code) {
    case 0x3000: // ideographic space
    case 0x00a0: // no-break space
    case 0x0009: // tab
      return 0x20;
    case 0x3002: // ideographic full stop
    case 0xff61: // half-width ideographic full stop
      return 0x2e;
    case 0x2212: // minus sign
    case 0x2013: // en dash
      return 0x2d;
    case 0x2044: // fraction slash
    case 0x2215: // division slash
      return 0x2f;
    case 0x2236: // ratio
      return 0x3a;
    default:
      return code;
  }
}

function normalizeInput(src: string): string {
  let out = '';
  for (let i = 0; i < src.length; i++) out += String.fromCharCode(normalizeChar(src.charCodeAt(i)));
  return out;
}

const isDigit = (c: string | undefined): boolean => c !== undefined && c >= '0' && c <= '9';
const isSpace = (c: string | undefined): boolean => c === ' ' || c === '\n' || c === '\r';
const NAME_CHAR = /[\p{L}\p{N}_]/u;

// ---------------------------------------------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------------------------------------------

interface Term {
  readonly value: BigRational;
  readonly end: number;
}

interface SecondsLiteral {
  readonly seconds: number;
  readonly end: number;
  /** The clock it was read from, for the field range check once the input is committed to seconds. */
  readonly clock: Clock;
}

/** A plain number of seconds or an `[h:]m:ss[.fff]` clock. */
interface Clock {
  readonly seconds: number;
  readonly end: number;
  readonly colons: number;
  /** Text and start index of each field, most significant first. */
  readonly fields: readonly { readonly text: string; readonly start: number }[];
}

/** Error key used when ctx.snapDiv is not a positive safe integer, by what needed it. */
type SnapUse = 'step' | 'rpeDecimal';

class Parser {
  readonly s: string;
  readonly ctx: BeatParseContext;

  constructor(s: string, ctx: BeatParseContext) {
    this.s = s;
    this.ctx = ctx;
  }

  ws(i: number): number {
    let j = i;
    while (isSpace(this.s[j])) j++;
    return j;
  }

  digits(i: number): number {
    let j = i;
    while (isDigit(this.s[j])) j++;
    return j;
  }

  /** Unsigned integer at i (required). */
  int(i: number): { readonly v: bigint; readonly end: number } {
    const end = this.digits(i);
    if (end === i) {
      if (i >= this.s.length) fail('time.parse.incomplete', i);
      fail('time.parse.expectedNumber', i, { pos: i + 1 });
    }
    return { v: BigInt(this.s.slice(i, end)), end };
  }

  snapDiv(at: number, use: SnapUse): bigint {
    const h = this.ctx.snapDiv;
    if (!Number.isSafeInteger(h) || h <= 0) {
      if (use === 'step') fail('time.parse.needsSnap', at);
      fail('time.parse.needsSnapDecimal', at);
    }
    return BigInt(h);
  }

  /** Grid for seconds input: the beat snap, or 1/1920 (free placement) when snapping is off or out of range. */
  secondsDiv(): number {
    const h = this.ctx.snapDiv;
    return Number.isSafeInteger(h) && h > 0 && h <= U32_MAX ? h : DEFAULT_SNAP_DIV;
  }

  // ----- seconds -------------------------------------------------------------------------------------------

  /** `num` or `[h:]m:ss[.fff]` clock at i; null when there is no number at i. */
  clock(i: number): Clock | null {
    const fields: { readonly text: string; readonly start: number }[] = [];
    let j = i;
    for (;;) {
      const start = j;
      j = this.digits(j);
      if (this.s[j] === '.') j = this.digits(j + 1);
      const text = this.s.slice(start, j);
      if (text.length === 0 || text === '.') return null;
      fields.push({ text, start });
      if (this.s[j] === ':' && fields.length < 3 && (isDigit(this.s[j + 1]) || this.s[j + 1] === '.')) {
        j += 1;
        continue;
      }
      break;
    }
    // Only the last field may carry decimals.
    for (let k = 0; k < fields.length - 1; k++) if ((fields[k]?.text ?? '').includes('.')) return null;
    let seconds = 0;
    for (const f of fields) seconds = seconds * 60 + Number(f.text);
    return { seconds, end: j, colons: fields.length - 1, fields };
  }

  /** Minutes and seconds below a higher field must be below 60 ("1:75" is a typo, not 2:15). */
  checkClockRange(clk: Clock): void {
    for (let k = 1; k < clk.fields.length; k++) {
      const f = clk.fields[k];
      if (f !== undefined && Number(f.text) >= 60) fail('time.parse.clockRange', f.start, { pos: f.start + 1 });
    }
  }

  /**
   * After '@' + number at j: is the first number written as a beat rather than as seconds? That is the case when
   * the number (a plain number or clock, as far as it reads like one) is followed by '/' or ';' (`37:1/4`, `49/4`,
   * `12;1`, also with spaces: `37 : 1 / 4`), or when an integer is followed by whitespace and another number (the
   * mixed number `12 3/4`, also while it is still being typed).
   */
  atBeatShaped(j: number): boolean {
    const clk = this.clock(j);
    if (clk === null) return false;
    const k = this.ws(clk.end);
    const c = this.s[k];
    if (c === '/' || c === ';') return true;
    if (c === ':' && clk.colons === 0) {
      // "37 : 1/4": the clock stopped at a spaced colon.
      const numStart = this.ws(k + 1);
      const numEnd = this.digits(numStart);
      return numEnd > numStart && this.s[this.ws(numEnd)] === '/';
    }
    const only = clk.fields[0];
    return clk.colons === 0 && only !== undefined && !only.text.includes('.') && k > clk.end && isDigit(c);
  }

  /** A unit suffix `s` / `ms` at i that ends the token; returns the multiplier and end. */
  unit(i: number): { readonly mul: number; readonly end: number } | null {
    const c = this.s[i];
    let mul: number;
    let end: number;
    if (c === 's' || c === 'S') {
      mul = 1;
      end = i + 1;
    } else if ((c === 'm' || c === 'M') && (this.s[i + 1] === 's' || this.s[i + 1] === 'S')) {
      mul = 0.001;
      end = i + 2;
    } else return null;
    const after = this.s[end];
    if (after !== undefined && NAME_CHAR.test(after)) return null;
    return { mul, end };
  }

  /** Start of the number after an '@' at i, or null when i is not '@' + number ('@name' is a marker). */
  atNumber(i: number): number | null {
    if (this.s[i] !== '@') return null;
    const j = this.ws(i + 1);
    return isDigit(this.s[j]) || this.s[j] === '.' ? j : null;
  }

  /** Seconds literal at i (`@clock`, `clock s`, `num ms`), or null when i does not start one. */
  seconds(i: number): SecondsLiteral | null {
    const j = this.atNumber(i);
    if (j !== null) {
      const clk = this.clock(j);
      if (clk === null) fail('time.parse.expectedNumber', j, { pos: j + 1 });
      const k = this.ws(clk.end);
      const u = this.unit(k);
      if (u !== null && (u.mul === 1 || clk.colons === 0)) {
        return { seconds: clk.seconds * u.mul, end: u.end, clock: clk };
      }
      return { seconds: clk.seconds, end: clk.end, clock: clk };
    }
    if (!isDigit(this.s[i]) && this.s[i] !== '.') return null;
    const clk = this.clock(i);
    if (clk === null) return null;
    const u = this.unit(this.ws(clk.end));
    if (u === null) return null;
    if (u.mul !== 1 && clk.colons > 0) return null;
    return { seconds: clk.seconds * u.mul, end: u.end, clock: clk };
  }

  // ----- beat terms ----------------------------------------------------------------------------------------

  markerName(i: number, prefix: string): { readonly name: string; readonly end: number } {
    if (this.s[i] === '"') {
      const close = this.s.indexOf('"', i + 1);
      if (close < 0) fail('time.parse.unclosedQuote', i);
      const name = this.s.slice(i + 1, close).trim();
      if (name.length === 0) fail('time.parse.markerName', i, { prefix });
      return { name, end: close + 1 };
    }
    let j = i;
    while (j < this.s.length) {
      const cp = this.s.codePointAt(j) ?? 0;
      const ch = String.fromCodePoint(cp);
      if (!NAME_CHAR.test(ch)) break;
      j += ch.length;
    }
    if (j === i) fail('time.parse.markerName', i, { prefix });
    return { name: this.s.slice(i, j), end: j };
  }

  marker(i: number, nameStart: number, prefix: string): Term {
    const { name, end } = this.markerName(nameStart, prefix);
    const lookup = this.ctx.marker;
    if (lookup === undefined) fail('time.parse.noMarkers', i);
    const beat = lookup(name);
    if (beat === undefined) fail('time.parse.unknownMarker', i, { name });
    return { value: beatToRat(beat), end };
  }

  /** Is there a marker reference at i (`m<name>`, `M<name>`, `@<name>`)? Returns the name start. */
  markerStart(i: number): number | null {
    const c = this.s[i];
    if (c === 'm' || c === 'M') {
      const n = this.s[i + 1];
      if (n === '"' || (n !== undefined && NAME_CHAR.test(n))) return i + 1;
      return null;
    }
    if (c === '@') {
      const j = this.ws(i + 1);
      const n = this.s[j];
      if (n === '"' || (n !== undefined && !isDigit(n) && NAME_CHAR.test(n))) return j;
    }
    return null;
  }

  term(i: number, markerAllowed: boolean): Term {
    const c = this.s[i];
    if (c === undefined) fail('time.parse.incomplete', i);
    const nameStart = this.markerStart(i);
    if (nameStart !== null) {
      if (!markerAllowed) fail('time.parse.markerFirst', i);
      return this.marker(i, nameStart, c === '@' ? '@' : c);
    }
    const atNum = this.atNumber(i);
    if (atNum !== null && this.atBeatShaped(atNum)) fail('time.parse.atFirst', i);
    if (this.seconds(i) !== null) fail('time.parse.mixedUnits', i);
    if (c === 'm' || c === 'M' || c === '@') fail('time.parse.markerName', i + 1, { prefix: c });
    if (!isDigit(c) && c !== '.') fail('time.parse.unexpected', i, { char: c, pos: i + 1 });

    const intEnd = this.digits(i);
    // Decimal: 12.75 (default) or 12.1 = 12 + 1/snapDiv (rpe).
    if (this.s[intEnd] === '.') {
      const fracEnd = this.digits(intEnd + 1);
      if (intEnd === i && fracEnd === intEnd + 1) fail('time.parse.expectedNumber', i, { pos: i + 1 });
      const after = this.s[this.ws(fracEnd)];
      if (after === '/' || after === ':' || after === ';') fail('time.parse.decimalInFraction', intEnd);
      const intPart = intEnd === i ? 0n : BigInt(this.s.slice(i, intEnd));
      const fracText = this.s.slice(intEnd + 1, fracEnd);
      if (this.ctx.syntax === 'rpe') {
        const h = this.snapDiv(intEnd, 'rpeDecimal');
        const k = fracText.length === 0 ? 0n : BigInt(fracText);
        return { value: Rat.of(intPart * h + k, h), end: fracEnd };
      }
      const value = Rat.fromDecimal(this.s.slice(i, fracEnd));
      if (value === null) fail('time.parse.expectedNumber', i, { pos: i + 1 });
      return { value, end: fracEnd };
    }

    const whole = BigInt(this.s.slice(i, intEnd));
    let j = this.ws(intEnd);
    const op = this.s[j];

    if (op === ':') {
      // 12:3/4
      j = this.ws(j + 1);
      if (j >= this.s.length) fail('time.parse.incomplete', j);
      if (!isDigit(this.s[j])) fail('time.parse.expectedFraction', j, { pos: j + 1 });
      return this.fractionAfterWhole(whole, j);
    }
    if (op === '/') {
      // 49/4
      const den = this.int(this.ws(j + 1));
      if (den.v === 0n) fail('time.parse.zeroDenominator', this.ws(j + 1));
      this.noDecimalAfter(den.end);
      return { value: Rat.of(whole, den.v), end: den.end };
    }
    if (op === ';') {
      // 12;1 = 12 + 1/snapDiv
      const h = this.snapDiv(j, 'step');
      const k = this.int(this.ws(j + 1));
      this.noDecimalAfter(k.end);
      return { value: Rat.of(whole * h + k.v, h), end: k.end };
    }
    if (j > intEnd && isDigit(op)) {
      // 12 3/4 (mixed number)
      return this.fractionAfterWhole(whole, j);
    }
    return { value: Rat.of(whole, 1n), end: intEnd };
  }

  /** `N/D` at i, added to `whole`. */
  fractionAfterWhole(whole: bigint, i: number): Term {
    const num = this.int(i);
    if (this.s[num.end] === '.') fail('time.parse.decimalInFraction', num.end);
    const slash = this.ws(num.end);
    if (this.s[slash] !== '/') {
      if (slash >= this.s.length) fail('time.parse.incomplete', slash);
      fail('time.parse.expectedFraction', slash, { pos: slash + 1 });
    }
    const denStart = this.ws(slash + 1);
    const den = this.int(denStart);
    if (den.v === 0n) fail('time.parse.zeroDenominator', denStart);
    this.noDecimalAfter(den.end);
    return { value: Rat.add(Rat.of(whole, 1n), Rat.of(num.v, den.v)), end: den.end };
  }

  noDecimalAfter(i: number): void {
    if (this.s[i] === '.' && isDigit(this.s[i + 1])) fail('time.parse.decimalInFraction', i);
  }
}

function toBeat(value: BigRational, at: number): Beat {
  const beat = beatFromRatExact(value);
  if (beat !== null) return beat;
  if (value.den > BigInt(U32_MAX)) fail('time.parse.tooPrecise', at);
  return fail('time.parse.tooLarge', at);
}

function echoOf(beat: Beat, ctx: BeatParseContext): string {
  const tempo = ctx.tempo;
  const text = `= ${formatBeat(beat)}`;
  return tempo === undefined ? text : `${text} · ${formatSec(tempo.secAt(beat))}`;
}

function parseOrThrow(src: string, ctx: BeatParseContext): BeatParseResult {
  const s = normalizeInput(src);
  const p = new Parser(s, ctx);
  let i = p.ws(0);
  if (i >= s.length) fail('time.parse.empty', i);

  let explicitAbsolute = false;
  if (s[i] === '=') {
    explicitAbsolute = true;
    i = p.ws(i + 1);
  }
  let sign: 1 | -1 = 1;
  let signed = false;
  if (s[i] === '+' || s[i] === '-') {
    sign = s[i] === '-' ? -1 : 1;
    signed = true;
    i = p.ws(i + 1);
  }
  if (i >= s.length) fail('time.parse.incomplete', i);
  const relative = signed && !explicitAbsolute;
  const bodyStart = i;

  // '@' followed by a number written as a beat (@37:1/4): the input is a beat expression, not seconds.
  const atNum = p.atNumber(i);
  const atBeats = atNum !== null && p.atBeatShaped(atNum);

  // ----- seconds -----
  const sec = atBeats ? null : p.seconds(i);
  if (sec !== null) {
    const after = p.ws(sec.end);
    if (after < s.length) {
      const c = s[after] ?? '';
      if (c === '+' || c === '-') fail('time.parse.mixedUnits', after);
      // "@1:" while typing a clock
      if (c === ':' && p.ws(after + 1) >= s.length) fail('time.parse.incomplete', p.ws(after + 1));
      fail('time.parse.unexpected', after, { char: c, pos: after + 1 });
    }
    p.checkClockRange(sec.clock);
    const tempo = ctx.tempo;
    if (tempo === undefined) fail('time.parse.needsTempo', bodyStart);
    const div = p.secondsDiv();
    const base = relative ? tempo.secAt(ctx.current ?? Beats.ZERO) : 0;
    const t = base + sign * sec.seconds;
    const x = tempo.beatF64At(t);
    if (!Number.isFinite(x)) fail('time.parse.badTime', bodyStart);
    let beat: Beat;
    try {
      beat = beatFromF64Snapped(x, div);
    } catch {
      return fail('time.parse.tooLarge', bodyStart);
    }
    const roundingMs = (tempo.secAt(beat) - t) * 1000;
    return { ok: true, beat, relative, echo: echoOf(beat, ctx), roundingMs };
  }

  // ----- sum of beat terms -----
  const first = atNum !== null && atBeats ? p.term(atNum, false) : p.term(i, !signed);
  let total = sign < 0 ? Rat.neg(first.value) : first.value;
  i = first.end;
  for (;;) {
    const j = p.ws(i);
    if (j >= s.length) break;
    const c = s[j] ?? '';
    if (c !== '+' && c !== '-') fail('time.parse.unexpected', j, { char: c, pos: j + 1 });
    const k = p.ws(j + 1);
    if (k >= s.length) fail('time.parse.incomplete', k);
    const t = p.term(k, false);
    total = c === '+' ? Rat.add(total, t.value) : Rat.sub(total, t.value);
    i = t.end;
  }

  if (relative) {
    const current = ctx.current;
    if (current === undefined) {
      const delta = toBeat(total, bodyStart);
      const mag = formatBeat(delta.w < 0 ? negBeat(delta) : delta);
      return { ok: true, beat: delta, relative, echo: `= ${delta.w < 0 ? '-' : '+'}${mag}` };
    }
    const beat = toBeat(Rat.add(beatToRat(current), total), bodyStart);
    return { ok: true, beat, relative, echo: echoOf(beat, ctx) };
  }
  const beat = toBeat(total, bodyStart);
  return { ok: true, beat, relative, echo: echoOf(beat, ctx) };
}

export const parseBeat: ParseBeat = (src, ctx) => {
  try {
    return parseOrThrow(src, ctx);
  } catch (e) {
    if (e instanceof ParseFail) return { ok: false, error: e.error, at: e.at };
    throw e;
  }
};

// ---------------------------------------------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------------------------------------------

export type { BarBeatParts, MeterInput, MeterMap, MeterMarkLike } from './meter';
export { barBeatParts, barBeatPartsForMeter, createMeterMap } from './meter';

function barBeatRefOf(parts: BarBeatParts): MessageRef {
  const frac = parts.frac === null ? '' : ` + ${String(parts.frac[0])}/${String(parts.frac[1])}`;
  return { key: 'time.barBeat', params: { bar: parts.bar, beat: parts.beat, frac } };
}

function barTextOf(parts: BarBeatParts): string {
  const head = `bar ${String(parts.bar)} . beat ${String(parts.beat)}`;
  return parts.frac === null ? head : `${head} + ${String(parts.frac[0])}/${String(parts.frac[1])}`;
}

/**
 * Translatable bar/beat label (key 'time.barBeat') for a constant meter counted from beat 0, the localisable
 * form of `formatBeat(b, 'bar', beatsPerBar)`. With meter changes use `barBeatRefForMeter`.
 */
export function barBeatRef(b: Beat, beatsPerBar?: number): MessageRef {
  return barBeatRefOf(barBeatParts(b, beatsPerBar));
}

/**
 * Translatable bar/beat label (key 'time.barBeat') under a meter with changes (`Timing.meter`, or a MeterMap
 * compiled once with `createMeterMap` for repeated use): bars restart at every meter mark (core/time/meter.ts).
 */
export function barBeatRefForMeter(b: Beat, meter: MeterInput): MessageRef {
  return barBeatRefOf(toMeterMap(meter).parts(b));
}

/** `formatBeat(b, 'bar')` text ("bar 4 . beat 1 + 1/4") under a meter with changes; debug/export form. */
export function formatBarForMeter(b: Beat, meter: MeterInput): string {
  return barTextOf(toMeterMap(meter).parts(b));
}

function formatColon(b: Beat): string {
  if (b.w < 0) {
    const m = negBeat(b);
    return `-${String(m.w)}:${String(m.n)}/${String(m.d)}`;
  }
  return `${String(b.w)}:${String(b.n)}/${String(b.d)}`;
}

function formatPlus(b: Beat): string {
  const neg = b.w < 0;
  const m = neg ? negBeat(b) : b;
  if (m.n === 0) return `${neg ? '-' : ''}${String(m.w)}`;
  const frac = `${String(m.n)}/${String(m.d)}`;
  if (m.w === 0) return `${neg ? '-' : ''}${frac}`;
  return neg ? `-${String(m.w)} - ${frac}` : `${String(m.w)} + ${frac}`;
}

export const formatBeat: FormatBeat = (b, style: BeatFormatStyle = 'colon', beatsPerBar) => {
  if (style === 'plus') return formatPlus(b);
  if (style === 'bar') return barTextOf(barBeatParts(b, beatsPerBar));
  return formatColon(b);
};

export const formatSec: FormatSec = (s) => {
  if (!Number.isFinite(s)) return '-:--.---';
  const ms = Math.round(Math.abs(s) * 1000);
  if (ms === 0) return '0:00.000';
  const minutes = Math.floor(ms / 60000);
  const rest = ms - minutes * 60000;
  const sec = Math.floor(rest / 1000);
  const milli = rest - sec * 1000;
  return `${s < 0 ? '-' : ''}${String(minutes)}:${String(sec).padStart(2, '0')}.${String(milli).padStart(3, '0')}`;
};
